import React from "react";
import { useTranslation } from "react-i18next";
import { Header, Segment, Message, Container, Button } from "semantic-ui-react";
import { useStore } from "../../../stores/store";

interface Props {
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
}

export default function PreSurveyIntroductionPage({ onNavigateNext, onNavigatePrevious }: Props) {
  const { t } = useTranslation("common");
  const { commonStore } = useStore();

  const content_en = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Introduction</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong> will help you to understand your level of competencies leading to
        identification of professional development needs. The tool provides you with a comprehensive
        feedback report with practical suggestions to improve your digital competence as a teacher. It
        also includes a glossary of key terms (See navigation at top right of the tool) related to
        questions and feedback items.
      </div>

      <div className="pre-post-txt">
        The questions and answers are linked to increasing levels of competence. Choose the answer that
        best fits. Once you have submitted your answers, you can download a feedback report indicating
        your level from Beginner to Pioneer.
      </div>
      <div className="pre-post-txt" style={{ textDecoration: "underline" }}>
        <strong>
          Please complete all the questions in one session, answering them as honestly and accurately as
          you can.
        </strong>
      </div>
      <div className="pre-post-txt">
        The data collected is treated with complete confidentiality and anonymity.
      </div>
      <Message color="yellow">
        <strong>IMPORTANT: </strong>
        Do not close your browser window until you have selected ‘Submit’ on the final page of the
        self-assessment. Otherwise, your inputs may be lost.
      </Message>
    </>
  );

  const content_uz = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Muqaddima</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong>sizning malaka darajangizni tushunishga yordam beradi, bu esa malaka
        oshirish ehtiyojlarini aniqlashga yordam beradi. Ushbu vosita sizga o'qituvchi sifatida raqamli
        kompetentsiyangizni yaxshilash bo'yicha amaliy takliflardan iborat to'liq keng qamrovli hisobotni
        taqdim etadi. Shuningdek, u savollar va fikr-mulohazalar bilan bog'liq asosiy atamalar lug'atini
        o'z ichiga oladi.
      </div>
      <div className="pre-post-txt">
        Savollar va javoblar kompetensiya darajasini oshirish bilan bog'liq. Sizga eng mos keladigan
        javobni tanlang. Javoblaringizni yuborganingizdan so'ng, Boshlovchidan Novatorgacha bo'lgan
        darajangizni ko'rsatadigan fikr-mulohazadan iborat hisobotni yuklab olishingiz mumkin. Iltimos,
        barcha savollarga imkon qadar ro‘yi-rost va toʻgʻri javob berib, ularga bitta seansda javob
        bering.
      </div>
      <div className="pre-post-txt">
        Yig‘ilgan ma’lumotlar bilan to‘liq maxfiylikka rioya qilingan holda ish olib boriladi va siz
        o’qituvchi sifatida bergan javoblar maxfiy saqlanadi.
      </div>

      <div className="pre-post-txt">
        <strong>
          Iltimos, barcha savollarni bir seansda tugating va ularga imkon qadar ro’yi-rost va aniq javob
          bering. Yig'ilgan ma'lumotlar to'liq maxfiylik va anonimlik bilan ishlanadi.
        </strong>
      </div>
      <Message color="yellow">
        <strong>MUHIM: </strong>
        O'z-o'zini baholashning yakuniy sahifasida "Yuborish" tugmachasini bosmaguningizcha brauzer
        oynangizni yopmang. Aks holda, kiritgan ma'lumotlaringiz yo'qolishi mumkin.
      </Message>
    </>
  );

  const content_ru = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Введение</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong>поможет вам понять уровень ваших компетенций, что приведет к выявлению
        потребностей в профессиональном развитии. Инструмент предоставляет вам подробный отчет с
        практическими предложениями по улучшению вашей цифровой компетентности в качестве учителя. Он
        также включает глоссарий ключевых терминов, связанных с вопросами и рекомендациями.
      </div>
      <div className="pre-post-txt">
        Вопросы и ответы связаны с ростом уровня компетентности. Выберите ответ, который наиболее
        подходит Вам. После того, как вы отправите свои ответы, вы сможете загрузить отчет с
        рекомендациями, в котором будет указан ваш уровень от Новичка до Новатора.
      </div>
      <div className="pre-post-txt">
        <strong>
          Пожалуйста, ответьте на все вопросы за один сеанс, отвечая на них максимально честно и точно.
          Собранные данные обрабатываются с полной конфиденциальностью и анонимностью.
        </strong>
      </div>

      <Message color="yellow">
        <strong>ВАЖНО: </strong>
        Не закрывайте окно браузера, пока не нажмете кнопку «Отправить» на последней странице самооценки.
        В противном случае ваши данные могут быть потеряны
      </Message>
    </>
  );

  return (
    <>
      {commonStore.lang === "en" && content_en}
      {commonStore.lang === "uz" && content_uz}
      {commonStore.lang === "ru" && content_ru}
      <Container className="survey-btns">
        <Segment>
          <Button
            primary
            floated="left"
            onClick={() => {
              onNavigatePrevious();
            }}
          >
            {t("Common.Previous")}
          </Button>

          <Button
            primary
            floated="right"
            onClick={() => {
              onNavigateNext();
            }}
          >
            {t("Common.Next")}
          </Button>
        </Segment>
      </Container>
    </>
  );
}
